import { auth } from '@mesa/core/auth/better-auth';
import { type AuthContext, scopesForRole } from '@mesa/core/auth/types';
import { DepotError } from '@mesa/core/lib/errors';
import { getOrgWithMembership } from '@mesa/core/org/queries';
import { trace } from '@opentelemetry/api';
import { createMiddleware } from '@tanstack/react-start';
import { getRequestHeaders } from '@tanstack/react-start/server';

export { assertScope } from './auth-utils';

export const sessionAuthMiddleware = createMiddleware({ type: 'function' }).server(async ({ next }) => {
  const headers = getRequestHeaders();
  const session = await auth.api.getSession({ headers });
  if (!session) {
    throw new Error('Unauthorized');
  }
  return next({ context: { session } });
});

export const baseAuthMiddleware = createMiddleware({ type: 'function' }).server(async ({ data, next }) => {
  const orgSlug = (data as unknown as { orgSlug: string }).orgSlug;
  if (!orgSlug || typeof orgSlug !== 'string') {
    throw new DepotError('INVALID_REQUEST', 'orgSlug is required');
  }
  const headers = getRequestHeaders();

  const session = await auth.api.getSession({ headers });
  if (!session) {
    throw new DepotError('UNAUTHORIZED', 'Unauthorized');
  }

  const result = await getOrgWithMembership(orgSlug, session.user.id);
  if (!result) {
    throw new DepotError('FORBIDDEN', 'Organization not found or user is not a member');
  }

  const impersonatedBy = (session.session as { impersonatedBy?: string }).impersonatedBy;

  const span = trace.getActiveSpan();
  if (span) {
    span.setAttributes({
      'enduser.id': session.user.id,
      'session.id': session.session.id,
      'mesa.org_id': result.org.id,
      'mesa.org_slug': orgSlug,
      'mesa.member_role': result.member.role,
      ...(impersonatedBy && { 'mesa.impersonated_by': impersonatedBy }),
    });
  }

  // The same AuthContext the API derives from a bearer token, so server
  // functions can call the core services the API routes call. Members are
  // scope-based through their role and never repo-restricted.
  const authContext: AuthContext = {
    org: { id: result.org.id, slug: result.org.slug, name: result.org.name },
    scopes: scopesForRole(result.member.role),
    keyId: null,
    repoIds: null,
    authors: null,
    credentialType: 'session',
    accessTokenGrant: null,
  };

  return next({
    context: {
      session,
      org: result.org,
      member: result.member,
      impersonatedBy,
      auth: authContext,
    },
  });
});
